// Where the people in a share write to each other, in the side panel for
// every kind (byj-1wv.51.3): a session's Notes, a terminal share's Chat
// (RFC-043 model 21) — the same notes, under the names byj-share, byj and the
// relay give them. They show as a conversation, oldest first and kept
// scrolled to the newest, then a card to write in for everyone the policy
// admits — the owner, coaches when notes are on, and spectators when `chat`
// is. A note is for people only: the agent and the terminal never see it,
// though byj shows it its owner. Asking the agent for something is not here
// but under the timeline.
import { useEffect, useId, useRef, useState, type FormEvent, type KeyboardEvent, type Ref } from "react";
import { t } from "../i18n";
import type { NoteItem } from "../timeline";
import type { Names } from "./Badge";
import { Messages } from "./Messages";
import { NoHover, useMedia } from "../media";

/** What it is called and says: a session's notes, or a terminal's chat. */
const words = {
  notes: () => ({ title: t("session:notes"), who: t("session:peopleOnly"), empty: t("session:noNotes"), placeholder: t("session:notePlaceholder"), send: t("session:sendNote"), closed: t("session:notesClosed"), ownerOnly: t("session:notesOwnerOnly") }),
  chat: () => ({ title: t("session:chat"), who: t("session:peopleOnlyTerminal"), empty: t("session:noChat"), placeholder: t("session:chatPlaceholder"), send: t("session:sendChat"), closed: t("session:chatClosed"), ownerOnly: t("session:chatOwnerOnly") }),
};

export function Chat({
  kind,
  notes,
  names,
  me,
  canSend,
  ownerOnly = false,
  disabled,
  why,
  onSend,
  onJump,
  over = false,
}: {
  /** a session's notes, or a terminal's chat */
  kind: keyof typeof words;
  notes: NoteItem[];
  names: Names;
  /** my user id: my messages are drawn on the right */
  me: string;
  /** This person may send: the policy admits their role. */
  canSend: boolean;
  /** The owner has turned notes off: only they may write. */
  ownerOnly?: boolean;
  disabled: boolean;
  /** why nothing can be written now, said in the composer while it is disabled */
  why?: string;
  onSend: (text: string) => boolean;
  /** a note on an event links to it in the timeline */
  onJump?: (seqs: readonly number[]) => void;
  /** The share has ended: what was said, and no composer. */
  over?: boolean;
}) {
  const w = words[kind]();
  const list = useRef<HTMLUListElement>(null);
  useEffect(() => {
    const el = list.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [notes.length]);
  return (
    <section className="panel chat side-fill" data-testid="chat" aria-label={w.title}>
      <div className="chat-head">
        <h3>{w.title}</h3>
        <span className="muted small">{w.who}</span>
      </div>
      {notes.length === 0 ? (
        <p className="muted chat-empty">{w.empty}</p>
      ) : (
        <Messages notes={notes} names={names} me={me} onJump={onJump} listRef={list} className="chat-messages side-fill-scroll" />
      )}
      {canSend && !over ? (
        <ChatForm placeholder={w.placeholder} sendLabel={w.send} disabled={disabled} why={why} onSend={onSend} />
      ) : (
        // once the share is over nobody writes, whatever their role
        !over && <p className="muted small chat-closed">{ownerOnly ? w.ownerOnly : w.closed}</p>
      )}
    </section>
  );
}

/**
 * The card a message is written in: Enter sends, Shift+Enter starts a new
 * line, as a chat does — but not on a touch screen, whose keyboard has no
 * Shift+Enter: there Enter is a new line and Send sends. Nor the Enter that
 * ends a Hangul (or any IME) syllable: Safari says that one is not
 * composing, with keyCode 229. `inline`, one line with Send beside it and no
 * hint under it (a watched session's note on the class dashboard). `stop`,
 * Stop in Send's place while what was sent is still being answered (the
 * workbench's agent at work), what is written meanwhile kept. `inputRef`,
 * the field, for the page to focus. Disabled, the card is drawn as one that
 * takes nothing, and `why`, where the page knows it, is said in the field
 * in the placeholder's place; its name for a screen reader stays, and the
 * reason is read with it as its description.
 */
export function ChatForm({ placeholder, label, sendLabel, disabled, onSend, stop, why, inputRef, inline = false, className }: {
  placeholder: string;
  /** the field's name for a screen reader, when it is not the placeholder */
  label?: string;
  sendLabel: string;
  disabled: boolean;
  /** true when it went, and the card is emptied */
  onSend: (text: string) => boolean;
  stop?: { label: string; onStop: () => void };
  /** why nothing can be sent now, said in the field while it is disabled */
  why?: string;
  inputRef?: Ref<HTMLTextAreaElement>;
  inline?: boolean;
  className?: string;
}) {
  const [text, setText] = useState("");
  const touch = useMedia(NoHover, false);
  const whyId = useId();
  const said = disabled && why ? why : undefined;
  const send = () => {
    const s = text.trim();
    if (s && onSend(s)) setText("");
  };
  const submit = (e: FormEvent) => {
    e.preventDefault();
    send();
  };
  const onKey = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (touch || e.key !== "Enter" || e.shiftKey || e.nativeEvent.isComposing || e.nativeEvent.keyCode === 229) return;
    e.preventDefault();
    send();
  };
  const button = stop ? (
    <button type="button" onClick={stop.onStop}>
      {stop.label}
    </button>
  ) : (
    <button type="submit" className="primary" disabled={disabled || !text.trim()}>
      {sendLabel}
    </button>
  );
  return (
    <form onSubmit={submit} className={`chat-form${disabled ? " chat-form-off" : ""}${className ? ` ${className}` : ""}`}>
      <textarea
        ref={inputRef}
        aria-label={label ?? placeholder}
        aria-describedby={said ? whyId : undefined}
        placeholder={said ?? placeholder}
        rows={inline ? 1 : 2}
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={onKey}
        disabled={disabled}
      />
      {said && (
        <span id={whyId} hidden>
          {said}
        </span>
      )}
      {inline ? (
        button
      ) : (
        <div className="chat-form-foot">
          <span className="muted small">{touch || disabled ? "" : t("session:chatKeys")}</span>
          {button}
        </div>
      )}
    </form>
  );
}
